import { useCallback, useState, useRef, useEffect } from 'react'
import type {
    MarkingEditorState,
    FontSizeRange,
    TextAlignment,
    FontFamily,
} from '../types'
import { clampFontSize } from '../utils'
import { getInitialState } from '../utils/state/stateUtils'
import { useMarkingInitialization } from './useMarkingInitialization'
import type { UseMarkingInitializationParams } from './useMarkingInitialization'

/**
 * Параметры для хука управления состоянием редактора маркировки
 */
export interface UseMarkingStateParams extends UseMarkingInitializationParams {
    /**
     * Начальное состояние (вычисляется через useMarkingInitialization)
     * Если не передано, будет вычислено автоматически
     */
    initialState?: MarkingEditorState
}

/**
 * Хук для управления локальным состоянием редактора маркировки
 * Отвечает только за управление состоянием, без логики инициализации
 */
export function useMarkingState(params?: UseMarkingStateParams) {
    const { initialState: providedInitialState, ...initParams } = params ?? {}

    // Сохраняем параметры инициализации для resetToCurrentData
    const initParamsRef = useRef(initParams)
    initParamsRef.current = initParams

    // Вычисляем начальное состояние через отдельный хук
    const computedInitialState = useMarkingInitialization(initParams)
    const initialState = providedInitialState ?? computedInitialState

    // Используем ref для хранения начального состояния
    const initialStateRef = useRef<MarkingEditorState>(initialState)

    // Обновляем ref при изменении начального состояния
    if (initialStateRef.current !== initialState) {
        initialStateRef.current = initialState
    }

    const [state, setState] = useState<MarkingEditorState>(() => initialState)

    // Обновляем ref при изменении начального состояния
    useEffect(() => {
        if (initialStateRef.current !== initialState) {
            initialStateRef.current = initialState
        }
    }, [initialState])

    // Функции-сеттеры для каждого поля
    const setMarkMedia = useCallback((value: boolean) => {
        setState((prev) => ({ ...prev, markMedia: value }))
    }, [])

    const setMarkMediaColor = useCallback((value: string) => {
        setState((prev) => ({ ...prev, markMediaColor: value }))
    }, [])

    const setMarkMediaText = useCallback((value: string | null) => {
        setState((prev) => ({ ...prev, markMediaText: value }))
    }, [])

    const setFontSize = useCallback((value: number) => {
        setState((prev) => ({
            ...prev,
            fontSize: clampFontSize(value),
        }))
    }, [])

    const setAlignment = useCallback((value: TextAlignment) => {
        setState((prev) => ({ ...prev, alignment: value }))
    }, [])

    const setFontFamily = useCallback((value: FontFamily) => {
        setState((prev) => ({ ...prev, fontFamily: value }))
    }, [])

    const setHasBackground = useCallback((value: boolean) => {
        setState((prev) => ({ ...prev, hasBackground: value }))
    }, [])

    const setBackgroundColor = useCallback((value: string | null) => {
        setState((prev) => ({ ...prev, backgroundColor: value }))
    }, [])

    const setPosition = useCallback((value: MarkingEditorState['position']) => {
        setState((prev) => ({ ...prev, position: value }))
    }, [])

    // Функция сброса к начальному состоянию (из ref)
    const reset = useCallback(() => {
        setState(initialStateRef.current)
    }, [])

    // Функция сброса к актуальным данным (пересчитывает состояние на лету)
    // Используется при открытии редактора после смены креатива
    const resetToCurrentData = useCallback(() => {
        const currentState = getInitialState(initParamsRef.current)
        initialStateRef.current = currentState
        setState(currentState)
    }, [])

    // Функция проверки наличия изменений
    // Вынесена в отдельный хук useMarkingChanges

    // Функция обновления всего состояния сразу
    const updateState = useCallback((updates: Partial<MarkingEditorState>) => {
        setState((prev) => ({ ...prev, ...updates }))
    }, [])

    return {
        state,
        setMarkMedia,
        setMarkMediaColor,
        setMarkMediaText,
        setFontSize,
        setAlignment,
        setFontFamily,
        setHasBackground,
        setBackgroundColor,
        setPosition,
        updateState,
        reset,
        resetToCurrentData,
        initialState: initialStateRef.current,
    }
}

export type UseMarkingStateReturn = ReturnType<typeof useMarkingState>
